import * as Yup from 'yup';

import type {
    AdminConsultantRole,
    ConsultantLicenseFormValues,
    CreateConsultantFormValues,
} from 'types';

const licenseSchema = (translate: (key: string) => string) =>
    Yup.object({
        id: Yup.number().optional(),
        country: Yup.string().oneOf(['AU', 'CA', 'UK']).required(translate('required')),
        license_number: Yup.string().trim().required(translate('required')),
        date_issued: Yup.string().required(translate('required')),
    });

const optionalLicenseSchema = (translate: (key: string) => string) =>
    Yup.object({
        id: Yup.number().optional(),
        country: Yup.string().oneOf(['AU', 'CA', 'UK']).required(translate('required')),
        license_number: Yup.string()
            .trim()
            .test('license-number-required-with-date', translate('required'), function (value) {
                const { date_issued } = this.parent as ConsultantLicenseFormValues;
                return !date_issued || Boolean(value?.trim());
            }),
        date_issued: Yup.string().test(
            'date-issued-required-with-license-number',
            translate('required'),
            function (value) {
                const { license_number } = this.parent as ConsultantLicenseFormValues;
                return !license_number?.trim() || Boolean(value);
            }
        ),
    });

const CONSULTANT_ROLES: AdminConsultantRole[] = ['CONSULTANT', 'ADMIN', 'SUPER_ADMIN'];

const hasUniqueLicenseCountries = (
    licenses: ConsultantLicenseFormValues[] | undefined
): boolean => {
    const countries = (licenses ?? [])
        .map((license) => license.country)
        .filter((country): country is string => Boolean(country));

    return new Set(countries).size === countries.length;
};

const hasLicenseDetails = (licenses: ConsultantLicenseFormValues[] | undefined): boolean =>
    (licenses ?? []).some((license) =>
        Boolean(license.license_number.trim() || license.date_issued)
    );

export const createConsultantInitialValues: CreateConsultantFormValues = {
    service_provider_id: '',
    firstname: '',
    middlename: '',
    lastname: '',
    email: '',
    role: 'CONSULTANT',
    licenses: [{ country: '', license_number: '', date_issued: '' }],
};

export const createConsultantValidationSchema = (translate: (key: string) => string) =>
    Yup.object({
        service_provider_id: Yup.number()
            .transform((value, originalValue) => (originalValue === '' ? undefined : value))
            .positive()
            .required(translate('required')),
        firstname: Yup.string().trim().required(translate('required')),
        middlename: Yup.string().trim(),
        lastname: Yup.string().trim().required(translate('required')),
        email: Yup.string().trim().email(translate('invalidEmail')).required(translate('required')),
        role: Yup.mixed<AdminConsultantRole>()
            .oneOf(CONSULTANT_ROLES)
            .required(translate('required')),
        licenses: Yup.array()
            .of(licenseSchema(translate))
            .min(1, translate('consultantLicenseRequired'))
            .required(translate('consultantLicenseRequired')),
    });

export const editConsultantValidationSchema = (translate: (key: string) => string) =>
    Yup.object({
        firstname: Yup.string().trim().required(translate('required')),
        middlename: Yup.string().trim(),
        lastname: Yup.string().trim().required(translate('required')),
        email: Yup.string().trim().email(translate('invalidEmail')).required(translate('required')),
        role: Yup.mixed<AdminConsultantRole>()
            .oneOf(CONSULTANT_ROLES)
            .required(translate('required')),
        licenses: Yup.array()
            .of(licenseSchema(translate))
            .min(1, translate('consultantLicenseRequired'))
            .required(translate('consultantLicenseRequired')),
    });

export const myLicensesValidationSchema = (translate: (key: string) => string) =>
    Yup.object({
        licenses: Yup.array()
            .of(optionalLicenseSchema(translate))
            .test('at-least-one-license', translate('consultantLicenseRequired'), hasLicenseDetails)
            .test(
                'unique-license-countries',
                translate('duplicateLicenseCountry'),
                hasUniqueLicenseCountries
            )
            .required(translate('consultantLicenseRequired')),
    });
